import {
  createSession as apiCreateSession,
  createSessionFromContext as apiCreateSessionFromContext,
  getSessions as apiGetSessions,
  getSessionDetails as apiGetSessionDetails,
  deleteSession as apiDeleteSession,
  cloneSession as apiCloneSession,
  updateSession as apiUpdateSession,
  getChildSessions as apiGetChildSessions,
  getAppInfo,
  importSession as apiImportSession,
  setActiveSession as apiSetActiveSession,
  getActiveSession as apiGetActiveSession,
  checkBackendHealth,
  stopStreaming as apiStopStreaming,
  cancelRequest as apiCancelRequest,
  deleteSessionInput as apiDeleteSessionInput,
  deleteSessionInputUntilHere as apiDeleteSessionInputUntilHere,
  retrySessionInput as apiRetrySessionInput,
  getInputWithLogs as apiGetInputWithLogs,
} from '@/lib/api';
import { UpdateSessionPayload, SessionInput, Agent } from '@/types/api';
import type { ReturnNavigation } from './state';
import { toast } from 'sonner';
import { SessionState, SessionStatusType, SessionSlice, BackendStatus } from './state';
import { StateCreator } from 'zustand';
import { useAgentStore } from '@/store/agentStore';

/**
 * Sort session inputs by sequence_number (primary) with created_at as
 * a deterministic tie-breaker. This ensures synthetic summary inputs
 * appear at their provider-timeline position (they inherit the max
 * source sequence_number) rather than at compression time.
 */
function sortInputsBySequence(inputs: SessionInput[]): SessionInput[] {
  return [...inputs].sort((a, b) => {
    const seqA = a.sequence_number ?? 0;
    const seqB = b.sequence_number ?? 0;
    if (seqA !== seqB) return seqA - seqB;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

export const createSessionSlice: StateCreator<SessionState, [], [], SessionSlice> = (set, get) => ({
  currentSessionId: null,
  currentSessionStatus: 'idle' as SessionStatusType,
  currentSessionDetails: null,
  activeAgentId: null as string | null,
  returnNavigation: null as ReturnNavigation | null,
  sessions: [],
  sessionsLoading: false,
  projectPath: null,
  isDevelopment: null,
  isLoading: false,
  error: null,
  backendStatus: 'connecting' as BackendStatus,
  quoteText: null,
  streamingResponse: { sessionInputId: null, content: '' },
  streamingThoughts: { sessionInputId: null, content: '' },
  streamingAborted: false,
  isRunning: false,
  lastSubmittedPrompt: null,
  editingRetryInputId: null as string | null,
  editingRetryPrompt: null as string | null,
  childSessions: [],
  setQuoteText: (quote) => set({ quoteText: quote }),
  setLastSubmittedPrompt: (prompt) => set({ lastSubmittedPrompt: prompt }),

  startStreamingResponse: (payload) => {
    set((state) => {
      const inputExists = state.currentSessionDetails?.sessionInputs?.some(
        (input) => input.id === payload.sessionInputId,
      );

      let newSessionDetails = state.currentSessionDetails;
      let newLatestInput = state.latestSessionInput;

      if (!inputExists && state.currentSessionDetails) {
        const tempInput: SessionInput = {
          id: payload.sessionInputId,
          session_id: state.currentSessionId!,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_prompt: null,
          context_template_id: null,
          ad_hoc_context_definition: null,
          generated_context_string: '',
          execution_strategy: '',
          raw_llm_response: null,
          llm_response_explanation: null,
          raw_studio_request_body: '',
          idempotency_key: null,
          is_context_carrier: false,
          aiActions: [],
          thoughts: null,
          role: 'model',
          external_conversation_id: null,
          external_turn_id: null,
          input_token_count: null,
          output_token_count: null,
          cached_token_count: null,
        };
        newSessionDetails = {
          ...state.currentSessionDetails,
          sessionInputs: [
            ...(state.currentSessionDetails.sessionInputs || []),
            tempInput,
          ],
        };
        newLatestInput = tempInput;
      }

      return {
        streamingResponse: {
          sessionInputId: payload.sessionInputId,
          content: payload.content || '',
        },
        streamingAborted: false,
        currentSessionDetails: newSessionDetails,
        latestSessionInput: newLatestInput,
      };
    });
  },

  appendStreamingResponse: (chunk) => {
    set((state) => ({
      streamingResponse: {
        ...state.streamingResponse,
        content: state.streamingResponse.content + chunk,
      },
    }));
  },

  clearStreamingResponse: () => {
    set({ streamingResponse: { sessionInputId: null, content: '' }, streamingThoughts: { sessionInputId: null, content: '' }, streamingAborted: false });
  },

  startStreamingThoughts: (payload) => {
    set((state) => {
      const inputExists = state.currentSessionDetails?.sessionInputs?.some(
        (input) => input.id === payload.sessionInputId,
      );

      let newSessionDetails = state.currentSessionDetails;
      let newLatestInput = state.latestSessionInput;

      if (!inputExists && state.currentSessionDetails) {
        const tempInput: SessionInput = {
          id: payload.sessionInputId,
          session_id: state.currentSessionId!,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_prompt: null,
          context_template_id: null,
          ad_hoc_context_definition: null,
          generated_context_string: '',
          execution_strategy: '',
          raw_llm_response: null,
          llm_response_explanation: null,
          raw_studio_request_body: '',
          idempotency_key: null,
          is_context_carrier: false,
          aiActions: [],
          thoughts: null,
          role: 'model',
          external_conversation_id: null,
          external_turn_id: null,
          input_token_count: null,
          output_token_count: null,
          cached_token_count: null,
        };
        newSessionDetails = {
          ...state.currentSessionDetails,
          sessionInputs: [
            ...(state.currentSessionDetails.sessionInputs || []),
            tempInput,
          ],
        };
        newLatestInput = tempInput;
      }

      return {
        streamingThoughts: {
          sessionInputId: payload.sessionInputId,
          content: payload.content || '',
        },
        currentSessionDetails: newSessionDetails,
        latestSessionInput: newLatestInput,
      };
    });
  },

  appendStreamingThoughts: (chunk) => {
    set((state) => ({
      streamingThoughts: {
        ...state.streamingThoughts,
        content: state.streamingThoughts.content + chunk,
      },
    }));
  },

  clearStreamingThoughts: () => {
    set({ streamingThoughts: { sessionInputId: null, content: '' } });
  },

  /**
   * Reconcile leftover streaming/generation state against the backend after a
   * WebSocket reconnect or tab visibility regain.
   *
   * When the browser tab is backgrounded/frozen, the WebSocket can drop, so the
   * backend's completion events (`llm-generation-ended` / `refresh-ui`) are lost
   * (they are broadcast only to currently-open sockets). On reconnect or tab
   * regain we re-fetch the active session and, if the backend reports it is no
   * longer running, clear all stale streaming state so the streaming UI hides
   * without a hard refresh.
   *
   * The network call is skipped when there is nothing stale to reconcile, so an
   * idle session reconnecting (or a quick blur/focus) does not trigger an extra
   * fetch.
   */
  reconcileStreamingState: async () => {
    const state = get();
    const { currentSessionId, isRunning, streamingResponse, streamingThoughts, llmGenerationStatus } = state;

    if (!currentSessionId) return;

    // Nothing stale to reconcile — avoid an unnecessary fetch.
    const hasStaleState =
      isRunning ||
      streamingResponse.sessionInputId !== null ||
      streamingThoughts.sessionInputId !== null ||
      llmGenerationStatus.status !== 'idle';
    if (!hasStaleState) return;

    try {
      await get().fetchSessionDetails(currentSessionId, 'refresh');
      // After the refresh, if the backend reports the session is not running,
      // clear all leftover streaming/generation state.
      if (!get().isRunning) {
        get().clearStreamingResponse();
        get().clearStreamingThoughts();
        get().resetLlmGenerationStatus();
      }
    } catch {
      // Non-critical: a failed reconciliation just leaves state as-is; the next
      // visibility/reconnect event will retry.
    }
  },

  setIsRunning: (isRunning: boolean) => {
    set({ isRunning });
  },

  updateSessionIsRunning: (sessionId: string, isRunning: boolean) => {
    set((state) => {
      const updatedSessions = state.sessions.map((s) =>
        s.id === sessionId ? { ...s, is_running: isRunning } : s,
      );
      const updatedCurrentSessionDetails =
        state.currentSessionDetails?.id === sessionId
          ? { ...state.currentSessionDetails, is_running: isRunning }
          : state.currentSessionDetails;
      const updatedChildSessions = state.childSessions.map((s) =>
        s.id === sessionId ? { ...s, is_running: isRunning } : s,
      );
      return {
        sessions: updatedSessions,
        currentSessionDetails: updatedCurrentSessionDetails,
        childSessions: updatedChildSessions,
      };
    });
  },

  stopStreaming: async () => {
    const sessionId = get().currentSessionId;
    if (!sessionId) return;

    try {
      await apiStopStreaming(sessionId);
      set({
        streamingAborted: true,
        streamingResponse: { sessionInputId: null, content: '' },
        streamingThoughts: { sessionInputId: null, content: '' },
        llmGenerationStatus: {
          sessionInputId: null,
          status: 'idle',
          isStreaming: false,
          error: null,
        },
        isRunning: false,
      });
      toast.success('Streaming stopped');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop streaming';
      toast.error(errorMessage);
    }
  },

  cancelRequest: async () => {
    const sessionId = get().currentSessionId;
    if (!sessionId) {
      return;
    }

    try {
      await apiCancelRequest(sessionId);
      set({
        llmGenerationStatus: {
          sessionInputId: null,
          status: 'idle',
          isStreaming: false,
          error: null,
        },
        streamingResponse: { sessionInputId: null, content: '' },
        streamingThoughts: { sessionInputId: null, content: '' },
        streamingAborted: true,
        isRunning: false,
      });
      toast.success('Request cancelled');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel request';
      toast.error(errorMessage);
    }
  },

  setCurrentSessionId: (id: string | null) => {
    set({
      currentSessionId: id,
      currentSessionStatus: id ? 'active' : 'idle',
      currentSessionDetails: id ? get().currentSessionDetails : null,
      latestSessionInput: id ? get().latestSessionInput : null,
      activePlanInput: id ? get().activePlanInput : null,
      activeSectionInputIds: null,
      autoScrollEnabled: id ? get().autoScrollEnabled : false,
      error: null,
    });
    if (id) {
      apiSetActiveSession(id).catch(err => {
        console.error("Failed to set active session on backend:", err);
        toast.error("Could not set active session on backend.");
      });
      get().fetchSessionDetails(id);
      get().fetchChildSessions(id);
      get().fetchWorkspaceFileTree();
      get().fetchAppInfo();
      // Batch fetch all application state in one call instead of 11 separate calls
      get().fetchAllApplicationState();
    } else {
      // Clear child sessions when no session is active
      set({ childSessions: [] });
      // No session active, nothing to reset
    }
  },

  startNewSession: async (sessionData?: { project_id?: string | null; session_title?: string | null; }) => {
    set({ isLoading: true, error: null, currentSessionDetails: null, latestSessionInput: null, activePlanInput: null, activeSectionInputIds: null, adHocFiles: [], adHocFolders: [], lastSubmittedPrompt: null, editingRetryInputId: null, editingRetryPrompt: null, autoScrollEnabled: false, activeAgentId: null });
    try {
      const newSession = await apiCreateSession(sessionData);
      set({
        currentSessionId: newSession.id,
        currentSessionStatus: newSession.status as SessionStatusType,
        currentSessionDetails: newSession,
        isLoading: false,
      });
      get().fetchWorkspaceFileTree();
      get().fetchAppInfo();
      // Batch fetch all application state in one call
      get().fetchAllApplicationState();

      // Apply default agent settings to the new session
      const { agents } = useAgentStore.getState();
      const defaultAgent = agents.find(a => a.is_default && a.is_active);
      if (defaultAgent) {
        set({ activeAgentId: defaultAgent.id });
        await get().selectAgent(defaultAgent);
      }

      toast.success(`New session "${newSession.session_title || newSession.id}" started.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start new session';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
    }
  },

  startFollowUpSession: async (sourceSessionId: string) => {
    set({ isLoading: true, error: null, currentSessionDetails: null, latestSessionInput: null, activePlanInput: null, activeSectionInputIds: null, adHocFiles: [], adHocFolders: [], lastSubmittedPrompt: null, editingRetryInputId: null, editingRetryPrompt: null, autoScrollEnabled: false });
    try {
      const newSession = await apiCreateSessionFromContext(sourceSessionId, { session_title: "Follow-up Session" });

      const sortedInputs = newSession.sessionInputs
        ? sortInputsBySequence(newSession.sessionInputs)
        : [];

      let adHocFiles: string[] = [];
      let adHocFolders: string[] = [];

      const latestInput = sortedInputs.length > 0 ? sortedInputs[sortedInputs.length - 1] : null;

      if (sortedInputs.length > 0) {
        const firstInput = sortedInputs[0];
        if (firstInput.is_context_carrier && firstInput.ad_hoc_context_definition) {
          try {
            const contextDef = JSON.parse(firstInput.ad_hoc_context_definition);
            adHocFiles = contextDef.files || [];
            adHocFolders = contextDef.folders || [];
            if (adHocFiles.length > 0 || adHocFolders.length > 0) {
              toast.info(`${adHocFiles.length} file(s) and ${adHocFolders.length} folder(s) loaded from previous session.`);
            }
          } catch (e) {
            console.error("Failed to parse ad-hoc context from follow-up session", e);
          }
        }
      }

      set({
        currentSessionId: newSession.id,
        currentSessionStatus: newSession.status as SessionStatusType,
        currentSessionDetails: { ...newSession, sessionInputs: sortedInputs },
        latestSessionInput: latestInput,
        activePlanInput: latestInput,
        isLoading: false,
        adHocFiles,
        adHocFolders,
      });
      get().fetchWorkspaceFileTree();
      get().fetchAppInfo();
      // Batch fetch all application state in one call
      get().fetchAllApplicationState();
      toast.success(`New follow-up session "${newSession.session_title || newSession.id}" started.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start follow-up session';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
    }
  },

  fetchSessionDetails: async (sessionId: string, mode: 'load' | 'refresh' = 'load') => {
    set({ isLoading: true, error: null });
    try {
      // Load session WITHOUT execution logs for fast initial load.
      // Logs are lazy-loaded per-input via fetchInputLogs.
      const sessionDetails = await apiGetSessionDetails(sessionId, false);

      const sortedInputs = sessionDetails.sessionInputs
        ? sortInputsBySequence(sessionDetails.sessionInputs)
        : [];

      const latestInput = sortedInputs.length > 0 ? sortedInputs[sortedInputs.length - 1] : null;

      // Only load ad-hoc context from a fresh follow-up session on first load.
      if (mode === 'load' && sortedInputs.length === 1) {
        const firstInput = sortedInputs[0];
        if (firstInput.is_context_carrier && firstInput.ad_hoc_context_definition) {
          try {
            const contextDef = JSON.parse(firstInput.ad_hoc_context_definition);
            const adHocFiles = contextDef.files || [];
            const adHocFolders = contextDef.folders || [];
            if (adHocFiles.length > 0 || adHocFolders.length > 0) {
              toast.info(`${adHocFiles.length} file(s) and ${adHocFolders.length} folder(s) loaded from previous session.`);
            }
            set({ adHocFiles, adHocFolders });
          } catch (e) {
            console.error("Failed to parse ad-hoc context from follow-up session", e);
            set({ adHocFiles: [], adHocFolders: [] });
          }
        }
      }

      // Add files/folders from a `request_context` action on session load.
      if (latestInput) {
        const { autoContextFetchEnabled } = get();
        const requestContextAction = latestInput.aiActions?.find(
          (a) => a.action_type === 'request_context',
        );
        if (requestContextAction && !autoContextFetchEnabled) {
          const files =
            requestContextAction.files
              ?.split(',')
              .map((f) => f.trim())
              .filter(Boolean) || [];
          const folders =
            requestContextAction.folders
              ?.split(',')
              .map((f) => f.trim())
              .filter(Boolean) || [];
          if (files.length > 0 || folders.length > 0) {
            get().addAdHocContext({ files, folders });
          }
        }
      }

      set({
        currentSessionDetails: { ...sessionDetails, sessionInputs: sortedInputs },
        currentSessionStatus: sessionDetails.status as SessionStatusType,
        isRunning: sessionDetails.is_running ?? false,
        latestSessionInput: latestInput,
        isLoading: false,
      });

      // Lazy-load execution logs for the latest input after initial render
      if (latestInput && latestInput.aiActions && latestInput.aiActions.length > 0) {
        get().fetchInputLogs(sessionId, latestInput.id);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch session details';
      set({ isLoading: false, error: errorMessage, currentSessionStatus: 'error' });
      toast.error(errorMessage);
    }
  },

  /**
   * Lazy-load execution logs for a single input and merge into currentSessionDetails.
   * This avoids loading ALL logs for ALL inputs upfront.
   */
  fetchInputLogs: async (sessionId: string, inputId: string) => {
    const currentDetails = get().currentSessionDetails;
    if (!currentDetails) return;

    // Check if logs are already loaded for this input (any action has executionLogs defined)
    const existingInput = currentDetails.sessionInputs?.find(i => i.id === inputId);
    if (existingInput?.aiActions?.some(a => a.executionLogs !== undefined && a.executionLogs !== null)) {
      return; // Already loaded
    }

    try {
      const inputWithLogs = await apiGetInputWithLogs(sessionId, inputId);

      // Merge the loaded actions (with executionLogs) into the session state
      set(state => {
        if (!state.currentSessionDetails) return state;

        const updatedInputs = (state.currentSessionDetails.sessionInputs || []).map(input => {
          if (input.id === inputId) {
            return {
              ...input,
              aiActions: inputWithLogs.aiActions || input.aiActions,
            };
          }
          return input;
        });

        const newLatestInput = state.latestSessionInput?.id === inputId
          ? { ...state.latestSessionInput, aiActions: inputWithLogs.aiActions || state.latestSessionInput.aiActions }
          : state.latestSessionInput;

        const newActivePlanInput = state.activePlanInput?.id === inputId
          ? { ...state.activePlanInput, aiActions: inputWithLogs.aiActions || state.activePlanInput.aiActions }
          : state.activePlanInput;

        return {
          currentSessionDetails: {
            ...state.currentSessionDetails,
            sessionInputs: updatedInputs,
          },
          latestSessionInput: newLatestInput,
          activePlanInput: newActivePlanInput,
        };
      });
    } catch (err) {
      console.error(`Failed to load logs for input ${inputId}:`, err);
    }
  },

  fetchChildSessions: async (parentSessionId: string) => {
    try {
      const childSessions = await apiGetChildSessions(parentSessionId);
      set({ childSessions });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch child sessions';
      console.error(errorMessage);
      toast.error(errorMessage);
    }
  },

  cloneSession: async (sessionId: string) => {
    try {
      const clonedSession = await apiCloneSession(sessionId);
      toast.success(`Session cloned: ${clonedSession.session_title || clonedSession.id.substring(0, 8)}`);
      // Refresh the sessions list
      await get().fetchSessions();
      return clonedSession;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clone session';
      toast.error(errorMessage);
      return null;
    }
  },

  fetchSessions: async (filter?: string) => {
    // Skip if already loading (prevents duplicate fetch in Strict Mode)
    if (get().sessionsLoading) return;
    
    set({ sessionsLoading: true, error: null });
    try {
      const sessions = await apiGetSessions(filter);
      set({ sessions: sessions.sort((a,b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()), sessionsLoading: false });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sessions';
      set({ sessionsLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  deleteSession: async (sessionId: string) => {
    const originalSessions = get().sessions;
    set(state => ({
      sessions: state.sessions.filter(s => s.id !== sessionId),
    }));
    try {
      await apiDeleteSession(sessionId);
      toast.success('Session deleted.');
      if (get().currentSessionId === sessionId) {
        get().clearSession();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete session.';
      set({ sessions: originalSessions, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  deleteSessionInput: async (sessionId: string, inputId: string) => {
    const originalDetails = get().currentSessionDetails;
    const originalLatest = get().latestSessionInput;
    const originalActivePlan = get().activePlanInput;

    set(state => ({
      currentSessionDetails: state.currentSessionDetails ? {
        ...state.currentSessionDetails,
        sessionInputs: (state.currentSessionDetails.sessionInputs || []).filter(input => input.id !== inputId),
      } : null,
      latestSessionInput: state.latestSessionInput?.id === inputId ?
        (state.currentSessionDetails?.sessionInputs || [])
          .filter(i => i.id !== inputId)
          .sort((a, b) => {
            const seqA = a.sequence_number ?? 0;
            const seqB = b.sequence_number ?? 0;
            if (seqA !== seqB) return seqA - seqB;
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          })
          .pop() || null : state.latestSessionInput,
      activePlanInput: state.activePlanInput?.id === inputId ? null : state.activePlanInput,
    }));

    try {
      await apiDeleteSessionInput(sessionId, inputId);
      toast.success('Turn deleted successfully.');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete turn.';
      set({
        currentSessionDetails: originalDetails,
        latestSessionInput: originalLatest,
        activePlanInput: originalActivePlan,
      });
      toast.error(errorMessage);
    }
  },

  deleteSessionInputUntilHere: async (sessionId: string, inputId: string) => {
    const originalDetails = get().currentSessionDetails;
    const originalLatest = get().latestSessionInput;
    const originalActivePlan = get().activePlanInput;

    const inputs = originalDetails?.sessionInputs || [];
    const targetIndex = inputs.findIndex(input => input.id === inputId);

    if (targetIndex === -1) {
      toast.error('Input not found');
      return;
    }

    const inputsToDelete = inputs.slice(targetIndex + 1);
    const idsToDelete = inputsToDelete.map(input => input.id);

    set(state => {
      const filteredInputs = (state.currentSessionDetails?.sessionInputs || [])
        .filter(input => !idsToDelete.includes(input.id));

      const newLatest = filteredInputs.length > 0
        ? filteredInputs[filteredInputs.length - 1]
        : null;

      return {
        currentSessionDetails: state.currentSessionDetails ? {
          ...state.currentSessionDetails,
          sessionInputs: filteredInputs,
        } : null,
        latestSessionInput: newLatest,
        activePlanInput: idsToDelete.includes(state.activePlanInput?.id || '') ? null : state.activePlanInput,
      };
    });

    try {
      await apiDeleteSessionInputUntilHere(sessionId, inputId);
      toast.success(`${inputsToDelete.length} turn(s) deleted successfully.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete turns.';
      set({
        currentSessionDetails: originalDetails,
        latestSessionInput: originalLatest,
        activePlanInput: originalActivePlan,
      });
      toast.error(errorMessage);
    }
  },

  importSession: async (file: File) => {
    set({ sessionsLoading: true, error: null });
    toast.loading('Importing session data...');
    try {
      const response = await apiImportSession(file);

      toast.dismiss();
      toast.success(`Import complete: ${response.message_count} messages imported.`, {
        description: `New session ID: ${response.session_id.substring(0, 8)}...`,
      });
      await get().fetchSessions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import session data.';
      set({ sessionsLoading: false, error: errorMessage });
      toast.dismiss();
      toast.error(errorMessage);
    }
  },

  updateSessionDefaults: async (payload: UpdateSessionPayload) => {
    const sessionId = get().currentSessionId;
    if (!sessionId) {
      toast.error("No active session to update.");
      return;
    }
    const originalDetails = get().currentSessionDetails;
    set(state => ({
      currentSessionDetails: state.currentSessionDetails ? {
        ...state.currentSessionDetails,
        ...payload
      } : null,
    }));
    try {
      const updatedSession = await apiUpdateSession(sessionId, payload);
      set({ currentSessionDetails: updatedSession });
      toast.success("Session defaults updated.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update session defaults';
      set({ currentSessionDetails: originalDetails });
      toast.error(errorMessage);
    }
  },

  /**
   * Select an agent preset and apply its configuration to the current session.
   * This copies all agent fields (system_prompt_id, context templates, model, reasoning_effort)
   * to the session using the existing updateSessionDefaults API.
   */
  selectAgent: async (agent: Agent) => {
    const sessionId = get().currentSessionId;
    if (!sessionId) {
      toast.error("No active session to update.");
      return;
    }

    const payload: UpdateSessionPayload = {};
    if (agent.system_prompt_id) payload.system_prompt_id = agent.system_prompt_id;
    if (agent.context_template_id) payload.default_initial_context_template_id = agent.context_template_id;
    if (agent.followup_context_template_id) payload.default_followup_context_template_id = agent.followup_context_template_id;
    if (agent.model_id) payload.model_id = agent.model_id;
    if (agent.reasoning_effort) payload.reasoning_effort = agent.reasoning_effort;

    // Only send non-null fields to avoid clearing existing values
    await get().updateSessionDefaults(payload);
  },

  /**
   * Clear the agent selection from the session by resetting all agent-controlled fields to null.
   * This doesn't change the current values — it just marks that no agent is active.
   * Since we don't store agent_id on the session, clearing is a no-op for the backend.
   * The UI uses activeAgentId state to track which agent is selected.
   */

  fetchAppInfo: async () => {
    if (get().projectPath) return;
    try {
      const appInfo = await getAppInfo();
      set({ projectPath: appInfo.project_path, isDevelopment: appInfo.is_development });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch app info.';
      console.error(errorMessage);
    }
  },

  clearSession: () => {
    set({
      currentSessionId: null,
      currentSessionStatus: 'idle',
      currentSessionDetails: null,
      activeAgentId: null,
      latestSessionInput: null,
      activePlanInput: null,
      activeSectionInputIds: null,
      isLoading: false,
      isActionLoading: {},
      isBatchActionLoading: false,
      isRunning: false,
      error: null,
      lastSubmittedPrompt: null,
      editingRetryInputId: null,
      editingRetryPrompt: null,
      autoScrollEnabled: false,
    });
    toast.info("Session cleared");
  },

  loadActiveSession: async () => {
    set({ backendStatus: 'connecting' });
    try {
      const activeSession = await apiGetActiveSession();
      
      if (activeSession) {
        toast.success(`Loaded active session: ${activeSession.session_title || activeSession.id.substring(0, 8)}`);
        get().setCurrentSessionId(activeSession.id);
      } else {
        toast.info("No active session found. Starting a new one.");
        await get().startNewSession({ session_title: "New Session" });
      }
      set({ backendStatus: 'connected' });
    } catch {
      // Backend unreachable — let the app load normally and navigate to instances page
      set({ backendStatus: 'connected' });
      // Remove bport param so the app stops trying to talk to the dead backend
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('bport')) {
          url.searchParams.delete('bport');
          window.history.replaceState({}, '', url.toString());
        }
      } catch {
        // URL manipulation can fail in some environments; non-critical
      }
      get().setActiveMainPage('instances');
    }
  },
  startRetryMode: (inputId: string, prompt: string) => {
    set({ editingRetryInputId: inputId, editingRetryPrompt: prompt });
    return prompt;
  },
  
  clearRetryMode: () => {
    set({ editingRetryInputId: null, editingRetryPrompt: null });
  },

  setActiveAgentId: (id: string | null) => {
    set({ activeAgentId: id });
  },

  setReturnNavigation: (nav: ReturnNavigation | null) => {
    set({ returnNavigation: nav });
  },
});